import { QueryKey, UseQueryOptions } from 'react-query'

import { useAppContext } from 'app/useAppContext'
import {
    queryClient,
    useCanRunStackScopedQueries,
    useCreateItem,
    useDeleteItem,
    useQuery,
    useQueryKeyBuilder,
    useUpdateItem,
} from 'data/hooks/_helpers'
import { STACK_QUERY_CONFIG } from 'data/reactQueryCache'

import { invalidateInstructions } from './instructions'
import { AgentSkill, AgentSkillSummary, SystemTool } from './types'

// Skills
const SKILLS_LIST_NAME = 'useAgentSkills'
const get_skills_endpoint = (agent_sid: string) => `agents/${agent_sid}/skills/`
const SYSTEM_SKILLS_ENDPOINT = `system-skills/`
const get_all_skill_summaries_endpoint = (stack_sid: string) => `stacks/${stack_sid}/agent-skills/`
const ALL_SKILLS_SUMMARIES_LIST_NAME = 'useAllSkillSummaries'
function useSkillsQueryKey(agent_sid?: string) {
    return useQueryKeyBuilder([SKILLS_LIST_NAME, agent_sid], {
        includeAuthKeys: true,
        includeStackId: true,
    })
}

function useAllSkillSummariesQueryKey() {
    return useQueryKeyBuilder([ALL_SKILLS_SUMMARIES_LIST_NAME], {
        includeAuthKeys: true,
        includeStackId: true,
    })
}
type SkillsOptionsType = UseQueryOptions<AgentSkill[], unknown, AgentSkill[], QueryKey>
type SkillSummariesOptionsType = UseQueryOptions<
    AgentSkillSummary[],
    unknown,
    AgentSkillSummary[],
    QueryKey
>

export function useAgentSkills(agent_sid: string, options: SkillsOptionsType = {}) {
    const enabled = useCanRunStackScopedQueries()
    const query_config = {
        ...(STACK_QUERY_CONFIG as SkillsOptionsType),
        keepPreviousData: true,
        ...options,
        enabled,
    }
    return useQuery<AgentSkill[]>(
        useSkillsQueryKey(agent_sid),
        get_skills_endpoint(agent_sid),
        query_config
    )
}

export function useAllSkillSummaries(options: SkillSummariesOptionsType = {}) {
    const enabled = useCanRunStackScopedQueries()
    const { selectedStack } = useAppContext()

    const query_config = {
        ...(STACK_QUERY_CONFIG as SkillSummariesOptionsType),
        keepPreviousData: true,
        ...options,
        enabled,
    }
    return useQuery<AgentSkillSummary[]>(
        useAllSkillSummariesQueryKey(),
        get_all_skill_summaries_endpoint(selectedStack?._sid || ''),
        query_config
    )
}

export function useSystemTools() {
    return useQuery<SystemTool[]>('useSystemTools', SYSTEM_SKILLS_ENDPOINT)
}

export function useCreateAgentSkill(agent_sid: string) {
    return useCreateItem<AgentSkill>(useSkillsQueryKey(agent_sid), get_skills_endpoint(agent_sid), {
        onSuccess: () => {
            invalidateSkills(agent_sid)
        },
    })
}

export function useUpdateAgentSkill(agent_sid: string) {
    return useUpdateItem<AgentSkill>(useSkillsQueryKey(agent_sid), get_skills_endpoint(agent_sid), {
        onSuccess: () => {
            invalidateSkills(agent_sid)
            invalidateInstructions(agent_sid)
        },
    })
}

export function useDeleteAgentSkill(agent_sid: string) {
    return useDeleteItem(useSkillsQueryKey(agent_sid), get_skills_endpoint(agent_sid), {
        onSuccess: () => {
            invalidateSkills(agent_sid)
        },
    })
}

export function useDuplicateSkill(agent_sid: string) {
    const createSkill = useCreateAgentSkill(agent_sid)

    const copySkill = (skill: AgentSkill) => {
        return {
            ...skill,
            _sid: undefined, // Remove the ID so a new one is created
            api_name: `${skill.api_name}_copy`,
            name: `${skill.name} Copy`,
        }
    }

    return {
        ...createSkill,
        mutate: (skill: AgentSkill) => {
            const duplicatedSkill = copySkill(skill)
            const result = createSkill.mutate(duplicatedSkill)
            return result
        },
        mutateAsync: async (skill: AgentSkill) => {
            const duplicatedSkill = copySkill(skill)
            const result = await createSkill.mutateAsync(duplicatedSkill)
            await refetchSkills(agent_sid)
            return result
        },
    }
}

export function invalidateSkills(agent_sid?: string) {
    return queryClient.invalidateQueries([SKILLS_LIST_NAME, agent_sid])
}

export function refetchSkills(agent_sid?: string) {
    return queryClient.refetchQueries([SKILLS_LIST_NAME, agent_sid])
}
